/**
 * MessageDetail — Right panel showing full message content, reply box, and actions.
 */
import { useState } from "react";
import { Send, ArrowUpRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import type { InboxMessage } from "@/types/inbox";

const urgencyVariant: Record<string, "danger" | "warning" | "default"> = {
  blocking: "danger",
  clarification: "warning",
  advisory: "default",
};

interface MessageDetailProps {
  message: InboxMessage;
  onReply: (content: string) => Promise<void>;
  onMarkRead: () => void;
}

export function MessageDetail({ message, onReply, onMarkRead }: MessageDetailProps) {
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);

  async function handleReply() {
    if (!replyText.trim()) return;
    setIsSending(true);
    try {
      await onReply(replyText.trim());
      setReplyText("");
    } finally {
      setIsSending(false);
    }
  }

  const variant = urgencyVariant[message.urgency] ?? "default";

  return (
    <div className="flex h-full flex-col" data-testid="message-detail">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{message.senderName}</h3>
          <div className="flex items-center gap-2">
            <Badge variant={variant}>{message.urgency}</Badge>
            <span className="text-xs uppercase text-muted">{message.channelType}</span>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {new Date(message.timestamp).toLocaleString()}
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          {message.content}
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-white/10 px-6 py-3">
        <div className="mb-3 flex gap-2">
          {!message.isRead && (
            <Button variant="ghost" size="sm" onClick={onMarkRead}>
              <CheckCircle className="h-4 w-4" />
              Mark Read
            </Button>
          )}
          <Button variant="ghost" size="sm" disabled>
            <ArrowUpRight className="h-4 w-4" />
            Escalate
          </Button>
        </div>

        {/* Reply box */}
        <div className="flex gap-2">
          <textarea
            data-testid="reply-input"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type a reply…"
            className="flex-1 resize-none rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                handleReply();
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleReply}
            loading={isSending}
            disabled={!replyText.trim()}
            className="self-end"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
